import { TableRow } from '@tiptap/extension-table';
import { createRoot } from 'react-dom/client';
import { Plugin } from '@tiptap/pm/state';
import { GripVertical } from 'lucide-react';
import InvestmentProductGroup from '@/components/editor/InvestmentProductGroup';
import { InvestmentProductPublicContent } from '@/components/blocks/InvestmentProductPublicRow';
import { publicInvestmentProduct } from '@/editor/investmentProduct';
import { isQuoteWorkspacePreview, selectWorkspacePosition } from '@/editor/quoteWorkspace';
import '@/components/editor/productPricing.css';
import ProductRowVersionFlag from '@/components/editor/ProductRowVersionFlag';
import { GroupVersionUpdate } from '@/components/editor/InvestmentInspector';
import { updateInvestmentProduct } from '@/editor/investmentProduct';
import { useInvestmentInspectorStore } from '@/store/investmentInspectorStore';
import { rowHasHeaderCell } from '@/editor/investmentTableRow';
import { investmentRowPricesVisible } from './investmentHiddenHours';
import { createRowDragController, moveInvestmentRow } from './investmentRowDelete';

/** One real table row per product: details cannot be edited or moved independently. */
export const InvestmentProductRow = TableRow.extend({
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = document.createElement('tr');
      if (!node.attrs.investmentProduct && !node.attrs.productPrice) {
        // Preserve the Pages TableRow layout for existing quote rows.
        const layout = (row: typeof node) => {
          dom.style.display = 'grid';
          dom.style.gridTemplateColumns =
            row.attrs.style ||
            Array.from({ length: row.childCount }, (_, index) =>
              row.child(index).attrs.colwidth ? `${row.child(index).attrs.colwidth}px` : '1fr'
          ).join(' ');
        };
        layout(node);
        return {
          dom,
          contentDOM: dom,
          update(next) {
            if (next.type !== node.type || next.attrs.investmentProduct || next.attrs.productPrice)
              return false;
            node = next;
            layout(next);
            return true;
          },
        };
      }
      dom.contentEditable = 'false';
      dom.dataset.investmentProduct = 'true';
      const cell = document.createElement('td');
      cell.colSpan = node.childCount;
      cell.style.padding = '0';
      dom.append(cell);
      const root = createRoot(cell);
      let disposed = false;
      let widths: { hourlyRate?: number; hours?: number; subtotal?: number } | undefined;
      let table: HTMLTableElement | null = null;
      let pricesVisible = true;
      const sorter = createRowDragController(editor);
      const observer = new ResizeObserver(() => {
        const overflow = isQuoteWorkspacePreview(editor) && dom.offsetHeight > 500;
        if (overflow !== (dom.dataset.productOverflow === 'true')) {
          if (overflow) dom.dataset.productOverflow = 'true';
          else delete dom.dataset.productOverflow;
        }
        const cells = table?.querySelector('tr')?.children;
        if (!cells || cells.length !== 4) return;
        const measured = Array.from(cells).map((cell) => (cell as HTMLElement).offsetWidth);
        const nextWidths = { hourlyRate: measured[1], hours: measured[2], subtotal: measured[3] };
        if (JSON.stringify(widths) !== JSON.stringify(nextWidths)) {
          widths = nextWidths;
          render(node);
        }
      });
      const render = (next: typeof node) => {
        let currency = 'EUR';
        const pos = getPos();
        pricesVisible = typeof pos === 'number' ? investmentRowPricesVisible(editor.state.doc, pos) : true;
        if (typeof pos === 'number') {
          const $pos = editor.state.doc.resolve(pos);
          for (let depth = $pos.depth; depth > 0; depth--) {
            const parent = $pos.node(depth);
            if (parent.type.name === 'investmentGroupNode')
              currency = parent.attrs.currency || 'EUR';
          }
        }
        cell.colSpan = next.childCount;
        queueMicrotask(() => {
          if (disposed) return;
          if (!table) {
            table = dom.closest('table');
            if (table) {
              observer.observe(table);
              table
                .querySelector('tr')
                ?.querySelectorAll('th,td')
                .forEach((cell) => observer.observe(cell));
            }
          }
          if (isQuoteWorkspacePreview(editor) || !editor.isEditable || next.attrs.productPrice) {
            const price =
              next.attrs.productPrice ?? publicInvestmentProduct(next.attrs.investmentProduct);
            const product = next.attrs.investmentProduct;
            const preview = isQuoteWorkspacePreview(editor) && product;
            const selected =
              editor.view.dom.dataset.workspaceSelection !== 'none' &&
              typeof pos === 'number' &&
              editor.state.selection.from > pos &&
              editor.state.selection.from < pos + next.nodeSize;
            let dataRows = 0;
            if (preview && typeof pos === 'number')
              editor.state.doc.resolve(pos).parent.forEach((row) => {
                if (!rowHasHeaderCell(row)) dataRows++;
              });
            root.render(
              <div
                data-product-group={preview ? product.id : undefined}
                className={
                  preview
                    ? 'workspace-preview-product' + (selected ? ' is-selected' : '')
                    : undefined
                }
                role={preview ? 'group' : undefined}
                tabIndex={preview ? 0 : undefined}
                aria-label={preview ? `${product.productName} bewerken` : undefined}
                onPointerDown={
                      preview
                    ? (event) => {
                        if ((event.target as Element).closest('button, input, select, a')) return;
                        event.preventDefault();
                        if (typeof pos === 'number') selectWorkspacePosition(editor, pos + 3);
                      }
                    : undefined
                }
                onKeyDown={
                  preview
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          if (typeof pos === 'number') selectWorkspacePosition(editor, pos + 3);
                        }
                      }
                    : undefined
                }
              >
                {preview && (
                  <button
                    type="button"
                    className="investment-row-drag-handle workspace-preview-product-drag"
                    title={dataRows > 1
                      ? 'Sleep om het product te verplaatsen · pijltjestoetsen werken ook'
                      : 'Voeg nog een rij toe om te sorteren'}
                    aria-label="Product verslepen"
                    disabled={dataRows <= 1}
                    onPointerDown={(event) => {
                      const current = getPos();
                      const group = event.currentTarget.closest<HTMLElement>('[data-product-group]');
                      if (typeof current === 'number' && group)
                        sorter.pointerDown(event.nativeEvent, group, current + 3);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
                      event.preventDefault();
                      event.stopPropagation();
                      const current = getPos();
                      if (typeof current !== 'number') return;
                      if (!moveInvestmentRow(editor, current + 3, event.key === 'ArrowUp' ? -1 : 1)) return;
                      // De rij wordt op zijn nieuwe plek opnieuw getekend; houd de focus op de greep.
                      requestAnimationFrame(() =>
                        editor.view.dom
                          .querySelector<HTMLElement>(
                            `[data-product-group="${CSS.escape(product.id)}"] .workspace-preview-product-drag`
                          )
                          ?.focus()
                      );
                    }}
                  >
                    <GripVertical size={14} />
                  </button>
                )}
                <InvestmentProductPublicContent
                  title={next.firstChild?.textContent ?? ''}
                  price={price}
                  currency={currency}
                  hoursVisible={price.showHours}
                  pricesVisible={pricesVisible}
                  widths={widths}
                  titleAdornment={isQuoteWorkspacePreview(editor) ? (
                    <ProductRowVersionFlag productId={next.attrs.productId} productVersionId={next.attrs.productVersionId}
                      onSelect={() => {
                        if (typeof pos === 'number') selectWorkspacePosition(editor, pos + 3);
                        useInvestmentInspectorStore.getState().requestReveal();
                      }}>
                      {product ? <GroupVersionUpdate product={product} onChange={(updated) => updateInvestmentProduct(editor, updated, true)} /> : undefined}
                    </ProductRowVersionFlag>
                  ) : undefined}
                />
              </div>
            );
          } else
            root.render(
              <InvestmentProductGroup
                product={next.attrs.investmentProduct}
                editor={editor}
                currency={currency}
                widths={widths}
                pricesVisible={pricesVisible}
              />
            );
        });
      };
      observer.observe(dom);
      const refresh = () => render(node);
      const refreshMode = ({
        transaction,
      }: {
        transaction: import('@tiptap/pm/state').Transaction;
      }) => {
        if (transaction.getMeta('workspaceModeChanged')) refresh();
        else if (transaction.docChanged) {
          // "Prijs per regel tonen" staat op de fasekop, niet op deze regel.
          const pos = getPos();
          if (typeof pos === 'number' && investmentRowPricesVisible(editor.state.doc, pos) !== pricesVisible)
            refresh();
        }
      };
      editor.on('selectionUpdate', refresh);
      editor.on('transaction', refreshMode);
      render(node);
      return {
        dom,
        update(next) {
          if (
            next.type !== node.type ||
            (!next.attrs.investmentProduct && !next.attrs.productPrice)
          )
            return false;
          node = next;
          render(next);
          return true;
        },
        stopEvent: () => true,
        ignoreMutation: () => true,
        destroy: () => {
          disposed = true;
          sorter.destroy();
          editor.off('selectionUpdate', refresh);
          editor.off('transaction', refreshMode);
          observer.disconnect();
          queueMicrotask(() => root.unmount());
        },
      };
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
      props: {
        handleKeyDown(view, event) {
          const $from = view.state.selection.$from;
          for (let depth = $from.depth; depth > 0; depth--) {
            if (!$from.node(depth).attrs.investmentProduct) continue;
            // Keep undo, navigation and clipboard shortcuts available.
              return (
                !event.metaKey &&
                !event.ctrlKey &&
                (event.key.length === 1 || ['Enter', 'Backspace', 'Delete'].includes(event.key))
              );
          }
          return false;
        },
      },
      }),
    ];
  },
});
